// Shot links: /ai-music-video/<projectId>?shot=S9.33&tab=image opens that
// shot's editor on the named tab. Support replies send these so a phone user
// lands on the control instead of hunting for one shot among 40+ on a long
// page (Michael Mulligan, Oct 22 2026: "open shot 9.33 in Studio, tap Image,
// then Use generated" was "like asking me to fly to the moon").

export type DirectorShotLinkTab = "image" | "video";

export type DirectorShotLinkTarget = {
  sceneNum: number;
  shotNum: number;
  /** null when the link names no tab (the editor opens on Image). */
  tab: DirectorShotLinkTab | null;
};

const SHOT_PARAM = "shot";
const TAB_PARAM = "tab";

/** "S9.33", "9.33", "s9-33" or "9_33" → scene 9, shot 33. */
export function parseDirectorShotParam(
  raw: string | null | undefined,
): { sceneNum: number; shotNum: number } | null {
  if (typeof raw !== "string") return null;
  const match = raw.trim().match(/^s?(\d{1,3})[.\-_](\d{1,3})$/i);
  if (!match) return null;
  return { sceneNum: Number(match[1]), shotNum: Number(match[2]) };
}

export function readDirectorShotLink(
  search: string,
): DirectorShotLinkTarget | null {
  const params = new URLSearchParams(search);
  const shot = parseDirectorShotParam(params.get(SHOT_PARAM));
  if (!shot) return null;
  const rawTab = (params.get(TAB_PARAM) || "").trim().toLowerCase();
  const tab: DirectorShotLinkTab | null =
    rawTab === "video" ? "video" : rawTab === "image" ? "image" : null;
  return { ...shot, tab };
}

/** True while the URL still carries a shot link (valid or not). */
export function hasDirectorShotLinkParam(search: string): boolean {
  return new URLSearchParams(search).has(SHOT_PARAM);
}

/** Index of the storyboard frame the link names, or -1. */
export function findDirectorShotLinkFrameIndex(
  frames: unknown,
  target: { sceneNum: number; shotNum: number },
): number {
  if (!Array.isArray(frames)) return -1;
  return frames.findIndex(
    (frame: any) =>
      Number(frame?.sceneNum) === target.sceneNum &&
      Number(frame?.shotNum) === target.shotNum,
  );
}

/** The search string without the shot-link params ("" when nothing is left),
 * so a reload or a shared URL doesn't reopen the editor. */
export function stripDirectorShotLinkParams(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(SHOT_PARAM);
  params.delete(TAB_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}

export function directorShotLinkUrl(
  origin: string,
  projectId: string,
  shot: { sceneNum: number; shotNum: number },
  tab?: DirectorShotLinkTab,
): string {
  const params = new URLSearchParams({
    [SHOT_PARAM]: `S${shot.sceneNum}.${shot.shotNum}`,
  });
  if (tab) params.set(TAB_PARAM, tab);
  return `${origin.replace(/\/+$/, "")}/ai-music-video/${encodeURIComponent(
    projectId,
  )}?${params.toString()}`;
}
